// pages/Register.js
import { useState } from 'react';
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, collection, query, where, getDocs, getDoc, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { validateCPF } from '../utils/validators';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';

function Register() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    cpf: '',
    category: '',
    password: '',
    confirmPassword: '',
  });
  const [profileImage] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { dispatch } = useApp();

  // Verificar se o usuário é admin
  const checkUserIsAdmin = async (userId) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      return userDoc.exists() ? userDoc.data()?.isAdmin === true : false;
    } catch (error) {
      console.error('Erro ao verificar status de admin:', error);
      return false;
    }
  };

  // Verificar se o usuário possui qualquer app
  const checkUserHasAnyApp = async (userId) => {
    try {
      const appsRef = collection(db, 'apps');
      const q = query(
        appsRef,
        where('userId', '==', userId),
        limit(1)
      );
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Erro ao verificar app do usuário:', error);
      return false;
    }
  };

  // Funções para enviar email via API - CORRIGIDAS
  const sendWelcomeEmail = async (userData) => {
    try {
      console.log('📧 Tentando enviar email para:', userData.email);

      // CORREÇÃO: Usar a rota correta /api/send-email
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'welcome',
          to: userData.email,
          name: userData.fullName,
          email: userData.email,
          phone: userData.phone,
          category: userData.category
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log('✅ Email de boas-vindas enviado para o cliente');
      } else {
        console.error('❌ Erro ao enviar email:', result.error);
      }
    } catch (error) {
      console.error('❌ Erro ao enviar email para o cliente:', error.message);
    }
  };

  const sendNotificationEmail = async (userData) => {
    try {
      console.log('📧 Tentando enviar notificação para CriaApp');

      // CORREÇÃO: Usar a rota correta /api/send-email
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'notification',
          name: userData.fullName,
          email: userData.email,
          phone: userData.phone,
          category: userData.category
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log('✅ Email de notificação enviado para a equipe');
      } else {
        console.error('❌ Erro ao enviar notificação:', result.error);
      }
    } catch (error) {
      console.error('❌ Erro ao enviar email de notificação:', error.message);
    }
  };

  // Função para formatar CPF
  const formatCPF = (cpf) => {
    return cpf
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .replace(/(-\d{2})\d+?$/, '$1');
  };

  // Função para formatar telefone
  const formatPhone = (phone) => {
    return phone
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'cpf') {
      formattedValue = formatCPF(value);
    } else if (name === 'phone') {
      formattedValue = formatPhone(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'fullName' || name === 'email') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
    }
  };

  // Função para verificar se o usuário já existe
  const checkIfUserExists = async (email, cpf) => {
    try {
      // Verificar se email já está em uso no Auth
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      if (signInMethods.length > 0) {
        return { exists: true, field: 'email', message: 'Este email já está em uso' };
      }

      // Verificar se CPF já está cadastrado no Firestore
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('cpf', '==', cpf));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return { exists: true, field: 'cpf', message: 'Este CPF já está cadastrado' };
      }

      return { exists: false };
    } catch (error) {
      console.error('Erro ao verificar usuário:', error);
      return { exists: false };
    }
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }

    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }

    const rawCPF = formData.cpf.replace(/\D/g, '');
    if (!validateCPF(rawCPF)) {
      setError('CPF inválido');
      return false;
    }

    if (!formData.email.includes('@')) {
      setError('Email inválido');
      return false;
    }

    if (formData.phone.replace(/\D/g, '').length < 10) {
      setError('Telefone inválido');
      return false;
    }

    if (!formData.category) {
      setError('Selecione uma categoria');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Verificar se usuário já existe
      const userCheck = await checkIfUserExists(formData.email, formData.cpf);
      if (userCheck.exists) {
        setError(userCheck.message);
        setLoading(false);
        return;
      }

      // Criar usuário no Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const user = userCredential.user;

      // ✅ ENVIAR EMAIL DE VERIFICAÇÃO
      await sendEmailVerification(user);
      console.log('📧 Email de verificação enviado pelo Firebase');

      sendWelcomeEmail(formData);
      sendNotificationEmail(formData);
    
      let imageUrl = '';

      // Fazer upload da imagem se existir
      if (profileImage) {
        try {
          const imageRef = ref(storage, `profileImages/${user.uid}`);
          const snapshot = await uploadBytes(imageRef, profileImage);
          imageUrl = await getDownloadURL(snapshot.ref);
        } catch (uploadError) {
          console.error('Erro no upload da imagem:', uploadError);
        }
      }

    // No seu Register.js, na parte do setDoc, ADICIONE:
await setDoc(doc(db, 'users', user.uid), {
  fullName: formData.fullName.trim(),
  email: formData.email.toLowerCase().trim(),
  phone: formData.phone,
  cpf: formData.cpf,
  category: formData.category,
  profileImage: imageUrl,
  createdAt: new Date(),
  updatedAt: new Date(),
  uid: user.uid,
  // ✅ ADICIONE ESTES CAMPOS:
  isNewUser: true,          // ← Marca como usuário novo
  registrationDate: new Date(), // ← Data de registro
  userStatus: 'pending'     // ← Status inicial
});

      console.log('✅ Usuário criado com sucesso! Campos para indicadores adicionados:', {
        uid: user.uid,
        readIndicators: {},
        firstLogin: true,
        isNewUser: true
      });

      // ✅ Ativar confetti via context
      dispatch({ type: 'SET_CONFETTI', payload: true });

      // Decidir redirecionamento imediato sem passar pelo step1
      const isAdmin = await checkUserIsAdmin(user.uid);
      if (isAdmin) {
        navigate('/admin', { replace: true });
        return;
      }

      const hasAnyApp = await checkUserHasAnyApp(user.uid);
      if (hasAnyApp) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/app-creation', { replace: true });
      }

    } catch (error) {
      console.error('Erro no registro:', error);

      let errorMessage = 'Erro ao criar conta. Tente novamente.';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Este email já está em uso. Por favor, faça login ou use outro email.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'A senha é muito fraca. Use pelo menos 6 caracteres.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido. Verifique o formato do email.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Operação não permitida. Contate o suporte.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-xl relative z-10">
        
        {/* Header Ultra Minimalista */}
        <div className="text-center mb-10">
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            Crie sua conta
          </h2>
          <p className="mt-2 text-sm text-gray-500 font-light">
            E comece a escalar seu aplicativo hoje mesmo
          </p>
        </div>

        <div className="bg-white py-10 px-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl border border-gray-100">
          {error && (
            <div className="mb-8 bg-red-50 border border-red-100 p-4 rounded-xl text-sm font-medium text-red-600">
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-5">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome completo
                </label>
                <input
                  name="fullName"
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-0 focus:border-black placeholder-gray-400 transition duration-300 focus:outline-none outline-none disabled:opacity-50"
                  placeholder="seu nome completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={loading}
                  className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-0 focus:border-black placeholder-gray-400 transition duration-300 focus:outline-none outline-none disabled:opacity-50"
                  placeholder="seu@email.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-0 focus:border-black placeholder-gray-400 transition duration-300 focus:outline-none outline-none disabled:opacity-50"
                    placeholder="(11) 99999-9999"
                    maxLength="15"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CPF
                  </label>
                  <input
                    name="cpf"
                    type="text"
                    required
                    value={formData.cpf}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-0 focus:border-black placeholder-gray-400 transition duration-300 focus:outline-none outline-none disabled:opacity-50"
                    placeholder="000.000.000-00"
                    maxLength="14"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria do negócio
                </label>
                <div className="relative">
                  <select
                    name="category"
                    required
                    value={formData.category}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-0 focus:border-black appearance-none transition duration-300 focus:outline-none outline-none disabled:opacity-50 cursor-pointer"
                  >
                    <option value="" disabled className="text-gray-400">Selecione uma categoria</option>
                    <option value="loja-virtual">Loja Virtual</option>
                    <option value="delivery">Delivery</option>
                    <option value="sistema-agendamento">Sistema de Agendamento</option>
                    <option value="catalogo">Catálogo</option>
                    <option value="outro">Outro</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Senha
                  </label>
                  <input
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-0 focus:border-black placeholder-gray-400 transition duration-300 focus:outline-none outline-none disabled:opacity-50"
                    placeholder="Mínimo 6 caracteres"
                    minLength="6"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar senha
                  </label>
                  <input
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 focus:bg-white focus:ring-0 focus:border-black placeholder-gray-400 transition duration-300 focus:outline-none outline-none disabled:opacity-50"
                    placeholder="Repita a senha"
                    minLength="6"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-3.5 px-4 rounded-xl shadow-sm text-sm font-medium text-white bg-[#0a0a0a] hover:bg-[#1a1a1a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  'Criar conta'
                )}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-50 text-center">
            <p className="text-sm text-gray-500">
              Já tem uma conta?{' '}
              <Link
                to="/login"
                className="font-semibold text-black hover:underline transition-all"
              >
                Fazer login
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-medium text-gray-400 hover:text-gray-900 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Voltar para a página inicial
          </Link>
        </div>

      </div>
    </div>
  );
}

export default Register;